import {
  useEffect,
  useCallback,
  useRef,
} from 'react';
import moment from 'moment';
import store from "./app/store";
import { createAction } from "@reduxjs/toolkit";
import { ACTION_AUTH_TOKEN_CHANGED } from "./auth/AuthReducer";
import { getIdleTimeoutPeriod } from "./system/system-settings/systemSettingsService";
import {
  normalizeIdleTimeoutMinutes,
  shouldLogoutForInactivity
} from "./sessionTimeout.utils";

const ACTIVITY_EVENTS = [
  'load',
  'mousemove',
  'mousedown',
  'click',
  'scroll',
  'keypress'
];

const IDLE_CHECK_DELAY_MS = 60000;

function hasAuthenticatedSession() {
  return !!sessionStorage.getItem('access_token');
}

function markSessionActivity() {
  sessionStorage.setItem('lastTimeStamp', moment().toISOString());
}

function clearSessionActivity() {
  sessionStorage.removeItem('lastTimeStamp');
}

function logoutForInactivity() {
  sessionStorage.removeItem('access_token');
  clearSessionActivity();
  store.dispatch(createAction(ACTION_AUTH_TOKEN_CHANGED)({ access_token: null }));
  window.location.href = `${process.env.PUBLIC_URL || ""}/login`;
}

function SessionTimeout() {
  const warningInactiveInterval = useRef();
  const startTimerInterval = useRef();

  const clearTimers = useCallback(() => {
    clearTimeout(startTimerInterval.current);
    clearInterval(warningInactiveInterval.current);
  }, []);

  const warningInactive = useCallback((idleTimeoutMinutes) => {
    clearTimeout(startTimerInterval.current);
    warningInactiveInterval.current = setInterval(() => {
      if (!hasAuthenticatedSession()) {
        clearTimers();
        clearSessionActivity();
        return;
      }

      const lastTimeStamp = sessionStorage.getItem('lastTimeStamp');
      if (!lastTimeStamp) {
        markSessionActivity();
        return;
      }

      if (shouldLogoutForInactivity(lastTimeStamp, idleTimeoutMinutes)) {
        clearTimers();
        logoutForInactivity();
      }
    }, 1000);
  }, [clearTimers]);

  const timeChecker = useCallback(() => {
    if (!hasAuthenticatedSession()) {
      clearTimers();
      clearSessionActivity();
      return;
    }

    startTimerInterval.current = setTimeout(async () => {
      try {
        const idleTimeout = await getIdleTimeoutPeriod();
        const idleTimeoutMinutes = normalizeIdleTimeoutMinutes(idleTimeout?.value);

        if (!idleTimeoutMinutes) {
          markSessionActivity();
          timeChecker();
          return;
        }

        warningInactive(idleTimeoutMinutes);
      } catch (error) {
        timeChecker();
      }
    }, IDLE_CHECK_DELAY_MS);
  }, [clearTimers, warningInactive]);

  const resetTimer = useCallback(() => {
    clearTimers();

    if (!hasAuthenticatedSession()) {
      clearSessionActivity();
      return;
    }

    markSessionActivity();
    timeChecker();
  }, [clearTimers, timeChecker]);

  useEffect(() => {
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    resetTimer();

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [clearTimers, resetTimer]);

  return null;
}

export default SessionTimeout;
